import React from 'react';
import { Card } from '../../../components/ui/Card';
import { Badge } from '../../../components/ui/Badge';
import { Callout } from '../../../components/ui/Callout';
import { Icon } from '../../../components/ui/Icon';
import { Divider } from '../../../components/ui/Divider';
import { ShippingRates } from '../../../components/calculator/components';
import { calculateTotalWeight } from '../../../components/calculator/utils/calculationUtils';
import { ShipStepNavigation } from './ShipStepNavigation';
import { useShippingWizard, ShippingSteps } from '../hooks/useShippingWizard';
import { useShippingHandlers } from '../hooks/useShippingHandlers';

interface Props {
  wizard: ReturnType<typeof useShippingWizard>;
  handlers: ReturnType<typeof useShippingHandlers>;
}

export const ShipStepRates: React.FC<Props> = ({ wizard: w, handlers: h }) => {
  const {
    isUpdatingRates,
    isLoadingRates,
    shippingRates,
    apiRates,
    selectedRateId,
    selectedService,
    formattedShippingCost,
    stepValidation,
    apiError,
    parcels,
    unitSystem,
    errorMessage,
    researchShippingRates,
    goToNextStep,
    locationState,
  } = w;

  return (
    <div>
      <Card pad style={{ padding: 28 }}>
        <h2 style={{ fontSize: 20, marginBottom: 4 }}>Service Selection</h2>
        <p className="muted" style={{ fontSize: 13, marginBottom: 20 }}>
          Choose your preferred shipping service based on delivery time and features.
        </p>

        {errorMessage && (
          <div style={{ marginBottom: 16 }}>
            <Callout tone="warn">
              <Icon name="alertCircle" size={14} />
              <span style={{ fontSize: 13 }}>{errorMessage}</span>
            </Callout>
          </div>
        )}

        <div style={{ position: 'relative', minHeight: 120 }}>
          {isUpdatingRates && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(255,255,255,0.9)',
                zIndex: 10,
                borderRadius: 8,
              }}
            >
              <div style={{ textAlign: 'center', padding: 24 }}>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    border: '3px solid var(--blue)',
                    borderTopColor: 'transparent',
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite',
                    margin: '0 auto 12px',
                  }}
                />
                <div style={{ fontWeight: 500, fontSize: 14 }}>Updating shipping rates…</div>
              </div>
            </div>
          )}

          {shippingRates.length > 0 ? (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <span style={{ fontWeight: 600, fontSize: 15 }}>Available Services</span>
                <Badge tone="default">
                  {shippingRates.length} option{shippingRates.length !== 1 ? 's' : ''}
                </Badge>
              </div>

              <ShippingRates
                hideSelectButton={true}
                visible={true}
                rates={apiRates || []}
                selectedRateId={selectedRateId}
                currency={selectedService?.currency || 'ILS'}
                isRTL={false}
                totalWeight={calculateTotalWeight(parcels)}
                unitSystem={unitSystem}
                onRateSelect={h.handleServiceSelect}
                onContinue={() => {}}
                showSortOptions={true}
                onRefresh={researchShippingRates}
                error={apiError}
              />
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <div className="muted" style={{ fontSize: 15, marginBottom: 12 }}>
                {isLoadingRates ? 'Loading shipping rates…' : 'No shipping rates available'}
              </div>
              {!isLoadingRates && (
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={researchShippingRates}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                >
                  <Icon name="refresh" size={14} />
                  Refresh Rates
                </button>
              )}
            </div>
          )}
        </div>

        {selectedService && (
          <>
            <Divider style={{ margin: '20px 0' }} />
            <div
              style={{
                padding: 16,
                borderRadius: 8,
                background: 'var(--surface-2)',
                border: '1px solid var(--border-ok, #a7f3d0)',
              }}
            >
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 10, color: 'var(--ok)' }}>
                Selected Service
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>{selectedService.name}</div>
                  <div className="muted" style={{ fontSize: 13 }}>by {selectedService.provider}</div>
                  <div className="muted" style={{ fontSize: 13 }}>Delivery: {selectedService.deliveryTime}</div>
                </div>
                <div className="mono" style={{ fontWeight: 600, fontSize: 22, color: 'var(--ok)' }}>
                  {formattedShippingCost.priceWithSymbol}
                </div>
              </div>
              {selectedService.features.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                  {selectedService.features.slice(0, 4).map((feature: string, idx: number) => (
                    <Badge key={idx} tone="info">{feature}</Badge>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </Card>

      <ShipStepNavigation
        onNext={() => {
          if (locationState) researchShippingRates();
          goToNextStep();
        }}
        nextLabel="Continue to Package Contents"
        nextDisabled={!stepValidation[ShippingSteps.SERVICE_SELECTION]}
        validationMessage={
          !stepValidation[ShippingSteps.SERVICE_SELECTION]
            ? 'Please select a shipping service'
            : undefined
        }
      />
    </div>
  );
};
